import {getLinkData} from 'Application/utils/linkFieldUtils';
import {isDefined, isDefinedAndPopulated} from 'Application/utils/typeGuards';

import type {LinkField, Media} from 'types/payload-types';

/**
 * A custom hook that extracts and normalizes data for the `Hero` component, including
 * link attributes for the call-to-action (CTA) button and image dimensions.
 * This hook leverages `getLinkData` to resolve the CTA button attributes (`href`, `label`,
 * `rel`, `target`). It also ensures safe handling of image dimensions, defaulting to `1x1`
 * if the provided image is undefined or does not include width/height metadata. This avoids
 * layout shifts or rendering errors when image data is incomplete.
 *
 * @param button Optional CTA button configuration, which may define a label, link type, and target.
 * @param image  Optional media object or string representing the hero image. If a media object includes `width` and `height`, those values will be used; otherwise defaults are applied.
 * @returns An object containing:
 * - `href`: The resolved URL for the CTA button.
 * - `label`: The label text for the CTA button.
 * - `rel`: The `rel` attribute for the button (if applicable).
 * - `target`: The `target` attribute for the button (e.g., `_blank` for external links).
 * - `width`: The image width, defaulting to `1` if not available.
 * - `height`: The image height, defaulting to `1` if not available.
 *
 * @example
 * ```tsx
 * const { href, label, width, height } = useHero(
 *   { label: "Get Started", linkType: "internal", internal: "/start" },
 *   { url: "/hero.png", width: 1200, height: 800 }
 * );
 *
 * // href: "/start"
 * // label: "Get Started"
 * // width: 1200
 * // height: 800
 * ```
 */
export const useHero = (button?: LinkField, image?: Media | string | null) => {
    const {href, label, rel, target} = getLinkData(button);
    let width = 1;
    let height = 1;

    if (isDefinedAndPopulated(image) && isDefined(image.width) && isDefined(image.height)) {
        width = image.width;
        height = image.height;
    }

    return {
        height,
        href,
        label,
        rel,
        target,
        width
    };
};